import type { ReactNode } from 'react';
import { Table } from '@tanstack/react-table';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';

import {
  DataTableFacetedFilter,
  DataTableFacetedFilterProps,
} from './DataTableFacetedFilter';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTableViewOptions } from './DataTableViewOptions';
import i18n from '@/utils/i18n';

export interface DataTableSelectionToolbarProps {
  selectedCount: number;
  totalRowCount: number;
  onSelectAllAcrossPages: () => void;
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  facetedFilters?: Array<
    Omit<DataTableFacetedFilterProps<TData, any>, 'column'> & {
      columnId: string;
    }
  >;
  isLoading?: boolean;
  RightComponent?: ReactNode;
  /** Left side of the toolbar row when rows are selected (same row as View / actions). */
  selectionToolbar?: DataTableSelectionToolbarProps | null;
  /** Trigger style for the column-visibility menu. */
  viewOptionsVariant?: 'button' | 'icon';
}

export function DataTableToolbar<TData>({
  table,
  facetedFilters,
  isLoading,
  RightComponent = null,
  selectionToolbar = null,
  viewOptionsVariant = 'button',
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;

  const st = selectionToolbar;
  const showSelectionSummary =
    !!st && st.totalRowCount > 0 && st.selectedCount > 0;

  return (
    <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
      <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-1">
        {isLoading && (
          <>
            <Skeleton className="tw:h-8 tw:w-24" />
            <Skeleton className="tw:h-8 tw:w-24" />
          </>
        )}
        {!isLoading && (
          <>
            {facetedFilters?.map((f) => {
              const col = table.getColumn(f.columnId);
              if (!col) {
                return null;
              }
              return (
                <DataTableFacetedFilter
                  key={f.columnId}
                  column={col}
                  title={f.title}
                  options={f.options}
                />
              );
            })}
          </>
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => table.resetColumnFilters()}
          >
            {i18n.t('Common:reset')}
            <X />
          </Button>
        )}
        {showSelectionSummary && st && (
          <>
            <span className="tw:text-sm tw:text-muted-foreground">
              {i18n.t('Common:rowsSelectedSummary', {
                selected: st.selectedCount,
                total: st.totalRowCount,
              })}
            </span>
            {st.selectedCount < st.totalRowCount && (
              <Button
                type="button"
                variant="link"
                className="tw:h-auto tw:p-0 tw:text-sm"
                onClick={st.onSelectAllAcrossPages}
              >
                {i18n.t('Common:selectAllItemsAcrossPages', {
                  count: st.totalRowCount,
                })}
              </Button>
            )}
          </>
        )}
      </div>
      <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
        <DataTableViewOptions table={table} variant={viewOptionsVariant} />
        {RightComponent}
      </div>
    </div>
  );
}
